import React, { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { X, Plus, MessageSquare } from 'lucide-react';
import { useAgentStore } from '@/stores/agentStore';
import { ChatHistoryDropdown } from './ChatHistoryDropdown';
import { AgentIndicator } from './AgentSelector';
import { summarizeTopic } from '../helpers';

interface AgentStatusBarProps {
  onNewChat: () => void;
  onClose: () => void;
  onLoadHistory: (entry: any) => void;
  onDeleteHistory: (id: string, e: React.MouseEvent) => void;
}

export const AgentStatusBar = React.memo(({ onNewChat, onClose, onLoadHistory, onDeleteHistory }: AgentStatusBarProps) => {
  const {
    messages, showHistoryModal, chatHistory, selectedHistoryId,
  } = useAgentStore(useShallow((s) => ({
    messages: s.messages,
    showHistoryModal: s.showHistoryModal,
    chatHistory: s.chatHistory,
    selectedHistoryId: s.selectedHistoryId,
  })));

  const topic = useMemo(() => {
    const firstUser = messages.find(m => m.role === 'user');
    if (firstUser) return summarizeTopic(firstUser.text);
    return '';
  }, [messages]);

  const hasMessages = messages.length > 0;

  return (
    <>
      <div className="flex items-center justify-between px-5 py-4 shrink-0">
        <div className="flex items-center gap-3">
          <AgentIndicator />
          <span className="text-[13px] font-medium text-white">{topic || (hasMessages ? '智能助手' : '')}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onNewChat}
            className="p-2 hover:bg-white/10 rounded-full text-slate-400 hover:text-white transition-colors"
            title="新建对话"
          >
            <Plus size={16} />
          </button>
          <button
            data-history-btn
            onClick={() => useAgentStore.getState().setShowHistoryModal(v => !v)}
            className="p-2 hover:bg-white/10 rounded-full text-slate-400 hover:text-white transition-colors"
            title="聊天历史"
          >
            <MessageSquare size={16} />
          </button>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full text-slate-400 hover:text-white transition-colors"
            title="关闭聊天"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {showHistoryModal && (
        <ChatHistoryDropdown
          chatHistory={chatHistory}
          selectedHistoryId={selectedHistoryId}
          onLoadHistory={onLoadHistory}
          onDeleteHistory={onDeleteHistory}
        />
      )}

      <div className="h-px bg-white/10 mx-5 shrink-0" />
    </>
  );
});
